import { useState, useEffect, Fragment } from 'react';
import { useRouter } from 'next/router';
import { Grid } from '@material-ui/core';

import { Season, SeasonAsSegment } from 'types/seasons';
import { EventAsSeasonSegment } from 'types/events';
import Page from 'layouts/Page';

import { organizationApi } from 'lib/api/organizationApi';
import { i18n } from 'lib/i18n';
import { seasonApi } from 'lib/api/seasonApi';

import LongDescription from 'components/shared/longDescription/LongDescription';
import ContactDetails from 'components/shared/ContactDetails/ContactDetails';
import Hero from 'components/shared/Hero/Hero';
import SeasonPicker from 'components/shared/SeasonPicker/SeasonPicker';
import SeasonScheduleEvent from 'components/shared/Spaces/SeasonScheduleEvent';
import SeasonDetails from 'components/shared/Spaces/SeasonDetails';
import { contactDataInit } from 'lib/organization';
import { getSportsIconsNames } from 'lib/sports';
import { rcEnums } from 'types/rc.enums';
import { getDaysAndTimeForActivityTimes, getTimeRangeDisplay, getTimes } from 'lib/dates';
import {
	isEarlyBird,
	isLateFeePrice,
	getSubSeasonName,
	getSubSeasonDateRange,
	SubSeasonItem,
	isRegistrationWindowOpenToAny,
} from 'lib/seasonUtils';
import { programApi } from 'lib/api/programApi';
import TwoColumnLayout from 'layouts/TwoColumn';

import { useBranding } from 'hooks/useBranding';
import { useDisclaimer } from 'hooks/useDisclaimer';
import { getTags } from 'lib/heroUtils';
import { css } from '@emotion/react';
import { Program } from '@bondsports/types';
import { Button, EBrandingColorsOptions } from '@bondsports/utils';
import { RCOrganization } from 'types/coreEntites/Organization';
import { getResourceSettingValueByKey } from '../../lib/resourceSettings';

import { ESessionResourceSettingKey } from '../../types/resource-settings';

const heroIconCss = css`
	div#heroIcons {
		display: flex;
	}
	div#heroTags {
		margin-top: 6px;
	}
`;

function getEvenTimes(subSeason: SubSeasonItem): string {
	if (subSeason.segmentType === rcEnums.shared.ResourceNameTypeEnum.PROGRAM_SEASON) {
		const activityTimes = (subSeason as SeasonAsSegment).activityTimes;
		if (activityTimes) {
			const rangeTimes = getDaysAndTimeForActivityTimes(activityTimes);
			return rangeTimes.join('\n');
		}
	} else if (subSeason.segmentType === rcEnums.shared.ResourceNameTypeEnum.EVENT) {
		return getTimeRangeDisplay(
			(subSeason as EventAsSeasonSegment).startTime,
			(subSeason as EventAsSeasonSegment).endTime
		);
	}
	return getTimes(subSeason.startDate, subSeason.endDate);
}

interface Props {
	programId: string;
	seasonId: string;
	program: Program;
	seasonData: Season;
	allProgramSeasonData: Season[];
	additionalProgramSeasons: Season[];
	organizationData: RCOrganization;
}

function Seasons({ seasonId, program, seasonData, additionalProgramSeasons, organizationData }: Props) {
	const router = useRouter();

	const [isRegistrationEnabled, setRegistrationEnabled] = useState(false);
	const [seasonMainMedia, setSeasonMainMedia] = useState<string>('');
	const [heroTitle, setHeroTitle] = useState<string>('');
	const [heroDescription, setHeroDescription] = useState<string>('');
	const [contactData, setContactData] = useState({ ...contactDataInit });
	const [programSeasons, setProgramSeasons] = useState<Season[]>([]);
	const [otherProgramSeasons, setOtherProgramSeasons] = useState<Season[]>([]);
	const [subSeasons, setSubSeasons] = useState<SubSeasonItem[]>([]);
	const [earlyBird, setEarlyBird] = useState(false);
	const [isLateFee, setIsLateFee] = useState(false);
	const [programSport, setProgramSport] = useState<number | null>(null);
	const [programName, setProgramName] = useState('');
	const [programLongDescription, setProgramLongDescription] = useState<string>('');
	const { setBranding } = useBranding();
	const { setDisclaimer } = useDisclaimer();

	const [isLoading, setLoading] = useState<boolean>(true);
	const [isLoadingSubs, setLoadingSubs] = useState<boolean>(false);
	const [isPastEventDisabled, setIsPastEventsDisabled] = useState<boolean>(false);
	const [tags, setTags] = useState<string[]>([]);

	const [isHideEventTimes, setIsHideEventTimes] = useState<boolean>(true);
	const [isHideSeasonSchedule, setIsHideSeasonSchedule] = useState<boolean>(true);

	const handleLoadPastSubSeasons = async () => {
		setLoadingSubs(true);
		await getSubSeasons(false);
		setLoadingSubs(false);
		setIsPastEventsDisabled(true);
	};

	const getSubSeasons = async (futureResources = true) => {
		const data = await seasonApi.getSubSeasons(Number(seasonId), futureResources);
		const sortedSubSeasonsData = data?.sort((a: SubSeasonItem, b: SubSeasonItem) =>
			a.startDate.localeCompare(b.startDate)
		);
		setSubSeasons(sortedSubSeasonsData);
		setRegistrationEnabled(isRegistrationWindowOpenToAny(data));
	};

	useEffect(() => {
		if (!seasonId || Array.isArray(seasonId)) {
			return;
		}

		if (!seasonData) {
			// can't do anything if couldn't find the season data
			return;
		}

		if (additionalProgramSeasons) {
			setOtherProgramSeasons(additionalProgramSeasons);
		}

		const { name: seasonName, description: seasonDescription, sport } = seasonData;

		let mainMedia = seasonData.mainMedia;
		if (!mainMedia?.url) {
			mainMedia = program?.mainMedia;
		}

		setProgramLongDescription(seasonData.longDescription ?? '');
		setProgramName(program?.name);

		setSeasonMainMedia(mainMedia?.url ?? '');
		setHeroTitle(seasonName);
		setHeroDescription(seasonDescription ?? '');
		setProgramSport(sport ?? rcEnums.shared.SportsEnum.BASKETBALL);

		setEarlyBird(isEarlyBird(seasonData));
		setIsLateFee(isLateFeePrice(seasonData));

		setProgramSeasons([seasonData]);

		setLoadingSubs(true);
		seasonApi.getSubSeasons(Number(seasonId), true).then(subSeasonsData => {
			setRegistrationEnabled(isRegistrationWindowOpenToAny(subSeasonsData));
			const sortedSubSeasonsData = subSeasonsData?.sort((a: SubSeasonItem, b: SubSeasonItem) =>
				a.startDate.localeCompare(b.startDate)
			);
			setSubSeasons(sortedSubSeasonsData);
		}).finally(() => {
			setLoadingSubs(false);
		});
		// Set resource settings
		setIsHideEventTimes(
			getResourceSettingValueByKey(seasonData.resourceSettings ?? [], ESessionResourceSettingKey.HIDE_EVENT_TIMES) ===
				'true'
		);

		setIsHideSeasonSchedule(
			getResourceSettingValueByKey(
				seasonData.resourceSettings ?? [],
				ESessionResourceSettingKey.HIDE_SEASON_SCHEDULE
			) === 'true'
		);

		setBranding(organizationData.brandings);
		setDisclaimer(organizationData.disclaimer);
		setLoading(false);
		setContactData(organizationData);
		setTags(getTags(seasonData));
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	if (isLoading) {
		return <div>{i18n.loading}</div>;
	}

	const sportsArr = programSport ? [programSport] : [];
	const sportsIconsNames = getSportsIconsNames(sportsArr);

	return (
		<Page showHeroBg heroBgImg={seasonMainMedia} css={heroIconCss}>
			<Hero>
				<Hero.Summary
					title={heroTitle}
					description={heroDescription}
					sports={sportsIconsNames}
					backHref={`${router.asPath}/../../../`}
					backTitle={programName}
					tags={tags}
				/>
				<Hero.Action>
					<SeasonPicker
						isRegistrationEnabled={isLoadingSubs || isRegistrationEnabled}
						program={programName}
						seasons={programSeasons}
						sport={programSport ?? rcEnums.shared.SportsEnum.OTHER}
						isHideEventTimes={isHideEventTimes}
					/>
				</Hero.Action>
			</Hero>

			<TwoColumnLayout>
				<TwoColumnLayout.Main>
					<TwoColumnLayout.Content>
						<LongDescription title={heroTitle} description={programLongDescription} />
					</TwoColumnLayout.Content>

					{isHideSeasonSchedule ? (
						<Fragment />
					) : (
						<TwoColumnLayout.Header data-testid="Session-schedule"
							title={i18n.season.seasonSchedule}
							titleComponent={
								<Button
									data-aid="button-Seasons"
									theme="secondary"
									sizer="XS"
									loadingColor={EBrandingColorsOptions.bg_text_secondary}
									isProcessing={isLoadingSubs}
									onClick={handleLoadPastSubSeasons}
									disabled={isPastEventDisabled}
								>
									{i18n.season.subSeason.getPastSeasonsButton}
								</Button>
							}
						>
							<Grid item sm={12}>
								<Grid container spacing={2} justify="space-between">
									{subSeasons?.map(subSeason => (
										<SeasonScheduleEvent
											key={subSeason.id}
											id={subSeason.id}
											eventName={getSubSeasonName(subSeason)}
											eventDatesRange={getSubSeasonDateRange(subSeason)}
											eventDateTime={getEvenTimes(subSeason)}
											isEarlyBird={earlyBird}
											isLateFee={isLateFee}
										/>
									))}
								</Grid>
							</Grid>
						</TwoColumnLayout.Header>
					)}

					{otherProgramSeasons.length !== 0 && (
						<TwoColumnLayout.Header title={i18n.season.moreProgramSeasons}>
							<Grid container spacing={2}>
								{otherProgramSeasons.slice(0, 3).map(seasonItem => (
									<Grid item sm={12} md={4} key={seasonItem.id}>
										<SeasonDetails {...seasonItem} />
									</Grid>
								))}
							</Grid>
						</TwoColumnLayout.Header>
					)}
				</TwoColumnLayout.Main>
				<TwoColumnLayout.Sidebar>
					<ContactDetails contactData={contactData} />
				</TwoColumnLayout.Sidebar>
			</TwoColumnLayout>
		</Page>
	);
}

export async function getServerSideProps(context: { query: { programId: string; seasonId: string } }) {
	const programId = context.query.programId;
	const seasonId = context.query.seasonId;

	const program = await programApi.getProgramData(Number(programId));
	const organizationId = Number(program?.organizationId);
	const seasonData = await seasonApi.getSeasonData(Number(seasonId));
	const allProgramSeasonData: Season[] = await seasonApi.getProgramSeasons(Number(programId));
	const additionalProgramSeasons = allProgramSeasonData?.filter(seasonItem => seasonItem.id !== Number(seasonId)) ?? [];
	const organizationData = await organizationApi.getOrganizationData(organizationId);

	return {
		props: {
			programId,
			seasonId,
			program,
			seasonData,
			allProgramSeasonData,
			additionalProgramSeasons,
			organizationData,
		},
	};
}

export default Seasons;
